import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, ArrowUpRight, X, ExternalLink, Maximize2 } from "lucide-react";

const CREDS = [
  {
    img: "/creds/apex-payout.webp",
    firm: "Apex Trader Funding",
    title: "Payout Withdrawn · $1,500",
    date: "Apr 2026",
    desc: "A real, approved withdrawal. Passing an evaluation is common talk — actually taking capital out is the part almost no one reaches.",
    href: "https://apextraderfunding.com",
    ratio: "aspect-[1320/879]",
  },
  {
    img: "/creds/apex-report.webp",
    firm: "Apex Trader Funding",
    title: "Funded Account · 75.86% Profitable",
    date: "Full sample",
    desc: "116 trades, +$3,250 gross P/L across a complete sample — a repeatable edge held under strict daily-loss and drawdown limits.",
    href: "https://apextraderfunding.com",
    ratio: "aspect-[1320/1000]",
  },
  {
    img: "/creds/topstep-funded.webp",
    firm: "TopStep",
    title: "Certified Funded Trader",
    date: "Jan 2026",
    desc: "Passed the Trading Combine and was allocated firm capital — profit target met without breaching a single risk rule.",
    href: "https://www.topstep.com",
    ratio: "aspect-[1320/1019]",
  },
  {
    img: "/creds/topstep-funded-2024.webp",
    firm: "TopStep",
    title: "Certified Funded Trader",
    date: "Jan 2024",
    desc: "The same result, two years earlier. Getting funded once can be luck; reaching it repeatedly is a process.",
    href: "https://www.topstep.com",
    ratio: "aspect-[941/703]",
  },
  {
    img: "/creds/topstep-combine.webp",
    firm: "TopStep",
    title: "$100K Combine · 100% Winning Days",
    date: "Evaluation",
    desc: "Eight trading days, every one green, on a $100K evaluation. Consistency under pressure — not a hot streak.",
    href: "https://www.topstep.com",
    ratio: "aspect-[1320/1087]",
  },
  {
    img: "/creds/express-funded.webp",
    firm: "TopStep",
    title: "$50K Express Funded Account",
    date: "Feb 2025",
    desc: "Cleared the live, funded stage that follows the Combine with a 75% winning-day rate — same rules-based execution, tighter timeline.",
    href: "https://www.topstep.com",
    ratio: "aspect-[1320/755]",
  },
  {
    img: "/creds/lucid-funded.webp",
    firm: "Lucid Trading",
    title: "LucidFlex 25K · Funded",
    date: "May 2026",
    desc: "A third, independent firm reaching the same outcome — funded under a rules-based drawdown model.",
    href: "https://lucidtrading.com",
    ratio: "aspect-[1320/922]",
  },
];

const GLANCE = [
  { v: "3", k: "Independent firms" },
  { v: "Multiple", k: "Funded accounts" },
  { v: "Withdrawn", k: "Real payout" },
  { v: "2024–26", k: "Verified span" },
];

const CONTEXT = [
  { v: "~7–10%", k: "pass a prop-firm evaluation" },
  { v: "~3–5%", k: "pass on the first attempt" },
  { v: "a fraction", k: "ever withdraw a payout" },
];

const SOURCES = [
  { label: "FunderPro", href: "https://funderpro.com/blog/prop-trading-pass-rates-in-2025-what-the-data-really-shows/" },
  { label: "Coinlaw", href: "https://coinlaw.io/ftmo-statistics/" },
  { label: "PipJournal", href: "https://pipjournal.ai/blog/prop-firm-pass-rate-statistics/" },
];

export const Credentials = () => {
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    const active = open || !!zoom;
    if (active) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open, zoom]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (zoom) setZoom(null);
      else if (open) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, zoom]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="view-credentials-btn"
        className="group mt-8 inline-flex items-center gap-3 border border-white/[0.10] bg-white/[0.03] px-5 py-3 hover:bg-[#BACD1C]/[0.12] transition-colors duration-300"
      >
        <ShieldCheck size={18} className="text-[#BACD1C]" />
        <span className="text-left leading-none">
          <span className="block font-display font-bold uppercase tracking-tight text-sm">View trading credentials</span>
          <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Independently funded · payout-verified</span>
        </span>
        <ArrowUpRight size={16} className="ml-1 text-white/40 group-hover:text-[#BACD1C] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="credentials-modal"
            data-lenis-prevent
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/85 backdrop-blur-md p-4 sm:p-8"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 260, damping: 26 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl border border-white/12 bg-[#0B0B0B] my-4"
            >
              {/* header */}
              <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-white/10 bg-[#0B0B0B]/95 backdrop-blur px-5 sm:px-9 py-4">
                <div className="flex items-center gap-2.5">
                  <ShieldCheck size={18} className="text-[#BACD1C]" />
                  <div className="leading-none">
                    <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#B4BAC4]">Trading credentials</div>
                    <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">Independently funded · payout-verified track record</div>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} data-testid="credentials-close" aria-label="Close" className="text-white/40 hover:text-white transition-colors">
                  <X size={20} />
                </button>
              </div>

              <div className="px-5 sm:px-9 py-8">
                {/* track record at a glance */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10">
                  {GLANCE.map((g) => (
                    <div key={g.k} className="bg-[#0B0B0B] p-4 sm:p-5">
                      <div className="font-display font-bold text-white text-glow text-2xl sm:text-[26px] tracking-tight">{g.v}</div>
                      <div className="mt-1.5 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-white/40 leading-relaxed">{g.k}</div>
                    </div>
                  ))}
                </div>

                {/* narrative — ties experience to the software without saying it outright */}
                <p className="mt-8 max-w-2xl font-term text-sm text-white/65 leading-relaxed">
                  Long before TradrQuant was a line of code, it was years at the screen — trading real capital and
                  clearing the evaluations independent proprietary firms use to separate disciplined operators from
                  the rest. <span className="text-white/90">You don't pass multiple firms on instinct.</span> It takes a repeatable system built to
                  survive the hardest rules in trading — daily-loss limits, trailing drawdown, consistency requirements —
                  where a single undisciplined trade ends the account.
                </p>

                <div className="mt-5 max-w-2xl border-l-2 border-[#BACD1C] pl-4 py-1">
                  <p className="font-term text-sm text-white/75 leading-relaxed">
                    That exact system — <span className="text-white/95">proven under the strictest rules and regulations in the industry</span> —
                    is what's being engineered into TradrQuant. What survives the hardest prop-firm rulebooks is
                    precisely what belongs in a retail terminal: <span className="text-[#BACD1C]">a rules-first edge, productized.</span>
                  </p>
                </div>

                {/* context stats */}
                <div className="mt-8">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/30 mb-3">For context — the industry odds</div>
                  <div className="border border-white/10 bg-[#0E0E0E]">
                    <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
                      {CONTEXT.map((s) => (
                        <div key={s.k} className="p-5">
                          <div className="font-display font-bold text-[#BACD1C] text-glow text-3xl tracking-tight">{s.v}</div>
                          <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white/45 leading-relaxed">{s.k}</div>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-white/10 px-5 py-3">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">Independent data:</span>
                      {SOURCES.map((src) => (
                        <a key={src.href} href={src.href} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.12em] text-[#BACD1C]/80 hover:text-[#BACD1C] transition-colors">
                          {src.label} <ExternalLink size={10} />
                        </a>
                      ))}
                    </div>
                  </div>
                  <p className="mt-3 font-mono text-[11px] text-white/40 leading-relaxed">
                    Passing — then <span className="text-white/70">actually withdrawing</span> — sits in the top few percent. That's the operating standard now built into the product.
                  </p>
                </div>

                {/* the ledger of proofs */}
                <div className="mt-9 flex items-center gap-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B4BAC4]">The receipts</span>
                  <span className="h-px flex-1 bg-gradient-to-r from-[#BACD1C]/30 to-transparent" />
                </div>

                <div className="mt-6 grid sm:grid-cols-2 gap-5">
                  {CREDS.map((c, i) => (
                    <div key={c.img} data-testid={`credential-card-${i}`} className="group/card border border-white/10 bg-[#0E0E0E] overflow-hidden hover:border-white/20 transition-colors duration-300">
                      <button
                        type="button"
                        onClick={() => setZoom(c.img)}
                        data-testid={`credential-image-${i}`}
                        className={`group relative block w-full ${c.ratio} bg-black overflow-hidden`}
                      >
                        <img src={c.img} alt={`${c.firm} — ${c.title}`} loading="lazy" className="absolute inset-0 h-full w-full object-contain" />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors duration-300">
                          <span className="flex items-center gap-1.5 border border-[#BACD1C]/50 bg-black/70 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[#B4BAC4] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <Maximize2 size={11} /> Enlarge
                          </span>
                        </span>
                        <span className="absolute left-2 top-2 font-mono text-[9px] tracking-[0.2em] text-white/30">{String(i + 1).padStart(2, "0")}</span>
                      </button>
                      <div className="p-4">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#BACD1C]/70">{c.firm}</span>
                          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/30">{c.date}</span>
                        </div>
                        <h3 className="mt-1.5 font-display font-bold uppercase tracking-tight text-[15px] leading-tight">{c.title}</h3>
                        <p className="mt-2 font-term text-[12px] text-white/50 leading-relaxed">{c.desc}</p>
                        <a href={c.href} target="_blank" rel="noopener noreferrer"
                          className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 hover:text-[#BACD1C] transition-colors">
                          Verify at {c.firm} <ExternalLink size={10} />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>

                <p className="mt-8 font-mono text-[10px] text-white/30 leading-relaxed">
                  Credentials issued by third-party firms and shown as provided. Funded and evaluation accounts operate in simulated environments.
                  Industry pass-rate figures are independent estimates, linked above. Trading involves substantial risk — past performance does not guarantee future results. Not financial advice.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* fullscreen image lightbox */}
      <AnimatePresence>
        {zoom && (
          <motion.div
            data-testid="credentials-lightbox"
            data-lenis-prevent
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setZoom(null)}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/95 p-4 sm:p-10"
          >
            <button onClick={() => setZoom(null)} aria-label="Close" data-testid="lightbox-close" className="absolute top-5 right-5 text-white/60 hover:text-white transition-colors">
              <X size={26} />
            </button>
            <motion.img
              src={zoom}
              alt="Credential"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[88vh] max-w-[92vw] object-contain border border-white/15"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
